import { Injectable } from '@angular/core';
import buildQuery from 'odata-query';
import { map, filter } from 'rxjs/operators';
import {
  FilterGroup,
  ODataFilter,
  ODataFilterValues,
  ODataSubFilter
} from '../shared/components/odata-filter/models/odata-filter';
import { SearchValueType } from '../shared/components/odata-filter/models/search-value-type';
import { SelectedFilter } from '../shared/components/odata-filter/models/selected-filter';
import {
  ACTIVE_CERTIFICATION_SUB_FILTER_MODELS,
  LINEUP_FORMS_SUB_FILTER_MODELS,
  PEOPLE_FILTER_MODELS,
  PERSON_CREDENTIALS_SUB_FILTER_MODELS,
  TEAM_HISTORY_SUB_FILTER_MODELS
} from '../shared/components/odata-filter/models/system-filter-models';
import { ViewContext } from '../shared/components/odata-filter/models/view-context';
import { AttributeDefinition } from '../shared/models/attributes/attributeDefinition';
import { AttributeEntityType } from '../shared/models/attributes/attributeEntityType';
import { AttributeUiType } from '../shared/models/attributes/attributeUiType';
import { CustomFieldFilterModel } from '../shared/odata-filter-models/custom-field-filter-model';
import { ODataFilterBuilder } from '../shared/odata-filter-models/odata-filter-builder';
import { AiFilterMapper } from '../ai-people-search/services/ai-filter-mapper';
import { AiPeopleQueryIntent } from '../ai-people-search/models/ai-people-intent.model';
import { BaseODataService, ODataQueryParams } from 'src/app/shared/services/base-odata.service';
import { FormService } from 'src/app/shared/services/form.service';
import { LookupService } from 'src/shared-app-portal/services/lookup.service';
import { TeamService } from 'src/app/shared/services/team.service';
import { PersonDetailsService } from 'src/app/shared/services/person-details.service';
import { TagService } from 'src/app/shared/services/tag.service';
import { IPeople, People } from './models/people.model';
import { PeopleFilterFields, PersonTeamFilterFields } from './models/peopleFilterFields';
import { PeopleFilterResult } from './models/peopleFilterResult.model';
import { ApiVersions } from 'src/app/shared/services/api-version';
import { FilterOperatorType } from '../shared/components/odata-filter/models/filter-operator-type';
import { PersonStatus } from 'src/app/people/models/personStatus';
import { Person } from './models/person';
import { EvaluationAggregateFieldFilterModel } from '../shared/odata-filter-models/evaluation-aggregate-field-filter.model';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { SubConditionFilterModel } from '../shared/odata-filter-models/sub-condition-filter-model';
import { ComplexFieldsFilterModel } from '../shared/odata-filter-models/complex-fields-filter-model';
import { AdminCredentialsService } from '../admin/admin-home/admin-credentials/admin-credentials.service';
import { ModuleGuardService } from '../guards/module-guard.service';
import { TenantModuleType } from '../shared/models/tenantModuleType';
import { CredentialDefinitionType } from '../shared/models/credential/credential-definition.model';
import {
  filterBySystemFieldVisibility,
  isSystemFieldEnabled
} from '../shared/utils/system-field-visibility.util';

@Injectable({
  providedIn: 'root'
})
export class PeopleFilterService extends BaseODataService {
  protected _datapointSelected: ODataFilter;
  protected _chartTypeSelected = 'Bar-Horizontal';
  protected _selectedPeopleIds: string[] = [];
  public readonly selectedPeopleIds = new BehaviorSubject<string[]>(this._selectedPeopleIds);
  private formsRequested = false;

  get selectedPeopleCount(): number {
    return this._selectedPeopleIds.length;
  }

  constructor(
    private lookupService: LookupService,
    private tagService: TagService,
    private teamService: TeamService,
    private formService: FormService,
    private personDetailsService: PersonDetailsService,
    private adminCredentialService: AdminCredentialsService,
    private moduleGuardService: ModuleGuardService
  ) {
    super();

    this._baseUrl = 'people';
    this._searchTextModel = PEOPLE_FILTER_MODELS.SearchFilterModel;
    this._filterResult = new PeopleFilterResult();
    this._chartResult = new PeopleFilterResult();
    this._viewContext = ViewContext.People;

    this._filterCollection = [
      PEOPLE_FILTER_MODELS.StatusFilterModel,
      PEOPLE_FILTER_MODELS.FirstNameFilterModel,
      PEOPLE_FILTER_MODELS.LastNameFilterModel,
      PEOPLE_FILTER_MODELS.FullNameFilterModel,
      PEOPLE_FILTER_MODELS.EmailFilterModel,
      PEOPLE_FILTER_MODELS.PhoneFilterModel,
      PEOPLE_FILTER_MODELS.ExternalSystemIdFilterModel,
      PEOPLE_FILTER_MODELS.JobTitleFilterModel,
      PEOPLE_FILTER_MODELS.CompanyFilterModel,
      PEOPLE_FILTER_MODELS.RatingFilterModel,
      PEOPLE_FILTER_MODELS.EmailPreferenceFilterModel,
      PEOPLE_FILTER_MODELS.GenderFilterModel,
      PEOPLE_FILTER_MODELS.LinkedInFilterModel,
      PEOPLE_FILTER_MODELS.CreatedOnFilterModel,
      PEOPLE_FILTER_MODELS.UpdatedOnFilterModel,
      PEOPLE_FILTER_MODELS.CountryFilterModel,
      PEOPLE_FILTER_MODELS.CountyFilterModel,
      PEOPLE_FILTER_MODELS.StateFilterModel,
      PEOPLE_FILTER_MODELS.ProvinceFilterModel,
      PEOPLE_FILTER_MODELS.CityFilterModel,
      PEOPLE_FILTER_MODELS.RaceFilterModel,
      PEOPLE_FILTER_MODELS.TagFilterModel,
      PEOPLE_FILTER_MODELS.NotesFilterModel,
      PEOPLE_FILTER_MODELS.SubmittedApplicationFilterModel,
      PEOPLE_FILTER_MODELS.LineupFormsFilterModel,
      PEOPLE_FILTER_MODELS.ActiveCertificationFilterModel,
      PEOPLE_FILTER_MODELS.CertificationsFilterModel,
      PEOPLE_FILTER_MODELS.TeamExperienceFilterModel,
      PEOPLE_FILTER_MODELS.TeamHistoryFilterModel,
      PEOPLE_FILTER_MODELS.PositionStatusFilterModel,
      PEOPLE_FILTER_MODELS.ActiveTeamsCountFilterModel,
      PEOPLE_FILTER_MODELS.AllTeamsCountFilterModel,
      PEOPLE_FILTER_MODELS.SkillsFilterModel
    ];

    if (this.hasCertMetricsIntegration) {
      this._filterCollection.push(PEOPLE_FILTER_MODELS.CertMetricsCandidateIdFilterModel);
    }

    this._filterCollection = this.applySystemFieldVisibility(this._filterCollection);

    if (this.authService.auth0SDKIntialised) {
      this.setCustomFields();
      this.setComplexCustomFields();
      this.setTeamHistoryFilters();
      this.setEvaluationQuestionFields();
      this.setOtherFilters();

      if (this.moduleGuardService.hasModule(TenantModuleType.Credentials)) {
        this.setCredentialFilters();
      }
    }

    this.personDetailsService.personCreated.subscribe((person) => {
      if (!person) return;
      this.addNewPersonToList(person);
    });

    this.personDetailsService.personUpdated.subscribe((person) => {
      if (!person) return;
      this.updatePerson(person);
    });

    this.personDetailsService.personDeleted.subscribe((person) => {
      if (!person) return;
      this.deletePerson(person.id);
    });

    this.teamService.personPicked.subscribe(() => {
      //when person picked, need to refresh the CurrentUpcomingTeams on the people page
      const peopleIds = this._filterResult.values.map((person) => person.id);
      if (peopleIds.length) {
        this.teamService.getPeopleCurrentUpcomingTeamsSummary(peopleIds);
      }
    });
  }
  protected setOrderBy(value: string[]) {
    if (!value.find((e) => e.includes(PeopleFilterFields.id))) {
      value.push(PeopleFilterFields.id);
    }
    super.setOrderBy(value);
  }

  public set chartDatapointSelected(value: ODataFilter) {
    this._datapointSelected = value;
  }
  public get chartDatapointSelected(): ODataFilter {
    return this._datapointSelected;
  }
  public set chartTypeSelected(value: string) {
    this._chartTypeSelected = value;
  }
  public get chartTypeSelected(): string {
    return this._chartTypeSelected;
  }

  get hasAnyNonActiveStatusInFilters(): boolean {
    return this._appliedFilters.findIndex((x) => this.hasNonActiveStatusFilter(x)) > -1;
  }

  get hasArchivedStatusInFilters(): boolean {
    return this._appliedFilters.findIndex((x) => this.hasArchivedStatusFilter(x)) > -1;
  }

  updateSelectedPeopleIds(ids: string[]) {
    this._selectedPeopleIds = ids;
    this.selectedPeopleIds.next(this._selectedPeopleIds);
  }

  applyExistingFilters() {
    this.applyFilter(this._appliedFilters);
  }

  /** Clears an AI Search result by resetting to an empty query. */
  applyAiFilter(odataQuery: string): void {
    this.applyQueryCode(odataQuery);
  }

  applyAiIntent(intent: AiPeopleQueryIntent): string {
    const root = intent?.filter ?? { logic: 'and', rules: [] };
    const isFlat = AiFilterMapper.isFlatAnd(root);

    if (this.selectedView) {
      this.selectedView.isAdvanced = !isFlat;
      this.selectedView.oDataQuery = null;
      this.selectedView.isModified = true;
    }

    if (isFlat) {
      this.applyFilter(AiFilterMapper.toSelectedFilters(root, this._filterCollection));
    } else {
      const rawFilter = AiFilterMapper.toFilterTree(root, this._filterCollection);
      if (this.selectedView) {
        this.selectedView.oDataQuery = buildQuery({ filter: rawFilter }).replace(
          /^\?\$filter=/,
          ''
        );
      }
      this.applyRawFilter(rawFilter);
    }
    return this.currentODataFilterQuery;
  }

  getAllPeopleIds(extraFilter: any) {
    let searchFilter = this.addFilterToCurrentFilters([extraFilter]);
    let params: ODataQueryParams = {
      select: ['id'],
      filter: searchFilter,
      // Must be `false`, not `null`: odata-query reads `count` as the filter source when
      // typeof count === 'object', and typeof null === 'object', which silently drops the
      // real filter and returns every person (see select-all regression).
      count: false,
      expand: null,
      orderBy: null
    };
    return this.getODataResult(this.allResultsFilterUrl, params, ApiVersions.unversioned).pipe(
      map((response) => {
        if (response && response.value) {
          return response.value.map((v) => People.parse(v));
        } else {
          return [];
        }
      })
    );
  }

  getAllPeopleIdsQuery(excludeArchived: boolean, extraFilter: any) {
    let searchFilter: any[];
    if (excludeArchived) {
      searchFilter = this.addFilterToCurrentFilters(
        ODataFilterBuilder.buildSimpleFilter(
          PeopleFilterFields.status,
          PersonStatus.Archived,
          FilterOperatorType.ne
        )
      );
    }
    if (extraFilter) {
      searchFilter = this.addFilterToCurrentFilters([extraFilter]);
    }
    let params: ODataQueryParams = {
      select: ['id'],
      filter: searchFilter,
      // Must be `false`, not `null`: odata-query reads `count` as the filter source when
      // typeof count === 'object', and typeof null === 'object', which silently drops the
      // real filter and returns every person (see select-all regression).
      count: false,
      expand: null,
      orderBy: null
    };

    return this.getODataQuery(this.allResultsFilterUrl, params, ApiVersions.unversioned);
  }

  selectResultSet() {
    this.getAllPeopleIds(null).subscribe((people) => {
      let ids = people.map((p) => p.id);
      this.updateSelectedPeopleIds(ids);
    });
  }

  protected addNewPersonToList(person: Person) {
    //adds a filter by the new person's id to the current filters to make sure the new person is matched to the current filter result.
    //(all current filters + personId eq '[person id]')
    //if the result is not null means the new person is matched with current filters, then adds it to the list
    let searchFilter = this.addFilterToCurrentFilters([
      ODataFilterBuilder.buildGuidFilter('id', person.id)
    ]);

    let params: ODataQueryParams = {
      select: null,
      filter: searchFilter,
      count: false,
      expand: this.expand,
      orderBy: this.orderBy
    };

    this.getODataResult(this.allResultsFilterUrl, params, ApiVersions.unversioned)
      .pipe(
        map((response) => {
          if (response && response.value && response.value.length) {
            return response.value[0];
          } else {
            return null;
          }
        }),
        filter((result) => result != null)
      )
      .subscribe((result: IPeople) => {
        //if the result is not null means the new person is matched with current filters
        if (result) {
          this._filterResult.values.unshift(People.parse(result));
          this._filterResult.count++;
        }
      });
  }

  protected updatePerson(person: Person) {
    let searchFilter = ODataFilterBuilder.buildGuidFilter('id', person.id);

    let params: ODataQueryParams = {
      select: null,
      filter: searchFilter,
      count: false,
      expand: this.expand,
      orderBy: this.orderBy
    };

    this.getODataResult(this.allResultsFilterUrl, params, ApiVersions.unversioned)
      .pipe(
        map((response) => {
          if (response && response.value && response.value.length) {
            return response.value[0];
          } else {
            return null;
          }
        }),
        filter((result) => result != null)
      )
      .subscribe((result: IPeople) => {
        let index = this._filterResult.values.findIndex((x) => x.id == result.id);
        if (index != -1) {
          this._filterResult.values[index] = People.parse(result);
        }
      });
  }

  private deletePerson(personId) {
    let index = this._filterResult.values.findIndex((x) => x.id == personId);
    if (index != -1) {
      this._filterResult.values.splice(index, 1);
      this._filterResult.count--;
    }
  }

  private setCustomFields() {
    this.attributeService.attributes
      .pipe(
        map((attributes) => {
          if (!attributes) return null;
          return attributes.filter((a) => a.filterable && a.uiType != AttributeUiType.Complex);
        })
      )
      .subscribe((result: AttributeDefinition[]) => {
        if (result && result.length > 0) {
          let customFields = result.filter(
            (field) =>
              field.entityType == AttributeEntityType.Person ||
              field.entityType == AttributeEntityType.Team
          );

          // Fills out skills filter values
          let skillsFilterModel = PEOPLE_FILTER_MODELS.SkillsFilterModel;
          skillsFilterModel.values = result
            .filter(
              (x) => x.entityType == AttributeEntityType.Person && x.uiType == AttributeUiType.Skill
            )
            .map((x) => new ODataFilterValues(x.id, x.name));
          if (this.systemFilterIndex(skillsFilterModel) != -1) {
            this._filterCollection[this.systemFilterIndex(skillsFilterModel)].values =
              skillsFilterModel.values;
          } else {
            super.setCustomFieldFilterCollection(skillsFilterModel);
          }

          //Adds customField filters
          customFields.forEach((item) => {
            const searchValueType = AttributeDefinition.getSearchValueType(item);
            if (!searchValueType) {
              return;
            }

            let targetField: any;
            let filterGroup: any;
            if (item.entityType == AttributeEntityType.Person) {
              targetField = PeopleFilterFields.customFields;
              filterGroup =
                item.uiType == AttributeUiType.Skill
                  ? FilterGroup.PersonSkills
                  : FilterGroup.PersonCustomFields;
            } else if (item.entityType == AttributeEntityType.Team) {
              targetField = PeopleFilterFields.teamExperienceFields;
              filterGroup = FilterGroup.TeamExperienceFields;
            } else {
              throw new Error('The attribute entity type is not valid ');
            }

            let customFieldFilterModel = new CustomFieldFilterModel(
              filterGroup,
              item.name,
              targetField,
              item.id,
              searchValueType,
              item.valueList.map((x) => new ODataFilterValues(x.id, x.itemValue))
            );

            super.setCustomFieldFilterCollection(customFieldFilterModel);
          });
        }

        this.filterCollection.next(this._filterCollection);
      });
  }

  private setComplexCustomFields() {
    this.attributeService.attributes
      .pipe(
        map((attributes) => {
          if (!attributes) return null;
          return attributes.filter(
            (a) =>
              a.entityType == AttributeEntityType.Person &&
              a.filterable &&
              a.uiType == AttributeUiType.Complex
          );
        })
      )
      .subscribe((complexFields: AttributeDefinition[]) => {
        if (complexFields && complexFields.length > 0) {
          complexFields.forEach((complexField) => {
            let complexCustomFieldFilterModel = new ComplexFieldsFilterModel(
              complexField.name,
              PeopleFilterFields.complexCustomFields,
              complexField.id
            );
            let subFieldFilters = complexField.subFieldList
              .filter((x) => x.subFieldDefinition.filterable)
              .map((subField) => {
                const searchValueType = AttributeDefinition.getSearchValueType(
                  subField.subFieldDefinition
                );
                if (!searchValueType) {
                  return;
                }

                return new SubConditionFilterModel(
                  subField.subFieldDefinition.name,
                  'values',
                  searchValueType,
                  subField.subFieldDefinition.valueList.map(
                    (x) => new ODataFilterValues(x.id, x.itemValue)
                  ),
                  subField.subFieldDefinition.id
                );
              });

            complexCustomFieldFilterModel.subFilter = new ODataSubFilter(subFieldFilters);

            super.setCustomFieldFilterCollection(complexCustomFieldFilterModel);
          });
        }
        this.filterCollection.next(this._filterCollection);
      });
  }
  public refreshPersonInList(personId: string): Observable<void> {
    let searchFilter = ODataFilterBuilder.buildGuidFilter('id', personId);

    let params: ODataQueryParams = {
      select: null,
      filter: searchFilter,
      count: false,
      expand: this.expand,
      orderBy: this.orderBy
    };

    return this.getODataResult(this.filterUrl, params, ApiVersions.v1_0).pipe(
      map((response) => {
        if (response?.value?.length) {
          let index = this._filterResult.values.findIndex((x) => x.id == response.value[0].id);
          if (index !== -1) {
            this._filterResult.values[index] = People.parse(response.value[0]);
          }
        }
      })
    );
  }
  private setTeamHistoryFilters() {
    let subs = combineLatest([
      this.teamService.teams,
      this.attributeService.attributes.pipe(
        map((attributes) => {
          if (!attributes) return null;
          return attributes.filter(
            (a) =>
              (a.entityType == AttributeEntityType.Team ||
                a.entityType == AttributeEntityType.Seat) &&
              a.filterable
          );
        })
      )
    ]);

    subs.subscribe(([teams, attributes]) => {
      if (teams && attributes) {
        let teamHistoryFilterModel = PEOPLE_FILTER_MODELS.TeamHistoryFilterModel;
        teamHistoryFilterModel.values = teams.map((x) => new ODataFilterValues(x.id, x.name));

        let customFieldFilterValues = attributes
          .filter((a) => a.entityType == AttributeEntityType.Team)
          .map((item) => {
            const searchValueType = AttributeDefinition.getSearchValueType(item);
            if (!searchValueType) {
              return;
            }

            return new SubConditionFilterModel(
              item.name,
              PersonTeamFilterFields.customFields,
              searchValueType,
              item.valueList.map((x) => new ODataFilterValues(x.id, x.itemValue)),
              item.id
            );
          });

        let trackedCustomFieldFilterValues = attributes
          .filter((a) => a.entityType == AttributeEntityType.Seat)
          .map((item) => {
            const searchValueType = AttributeDefinition.getSearchValueType(item);
            if (!searchValueType) {
              return;
            }

            return new SubConditionFilterModel(
              item.name,
              PersonTeamFilterFields.trackedCustomFields,
              searchValueType,
              item.valueList.map((x) => new ODataFilterValues(x.id, x.itemValue)),
              item.id
            );
          });

        let subFilters = [
          TEAM_HISTORY_SUB_FILTER_MODELS.TeamHistoryNameFilterModel,
          TEAM_HISTORY_SUB_FILTER_MODELS.TeamHistoryStatusFilterModel,
          TEAM_HISTORY_SUB_FILTER_MODELS.TeamHistoryPositionNameFilterModel,
          TEAM_HISTORY_SUB_FILTER_MODELS.TeamHistoryPositionStatusFilterModel,
          TEAM_HISTORY_SUB_FILTER_MODELS.SeatHistoryStartDateFilterModel,
          TEAM_HISTORY_SUB_FILTER_MODELS.SeatHistoryEndDateFilterModel,
          TEAM_HISTORY_SUB_FILTER_MODELS.TeamHistoryStartDateFilterModel,
          TEAM_HISTORY_SUB_FILTER_MODELS.TeamHistoryEndDateFilterModel
        ];

        subFilters = subFilters.concat(customFieldFilterValues, trackedCustomFieldFilterValues);

        teamHistoryFilterModel.subFilter = new ODataSubFilter(subFilters);

        if (this.systemFilterIndex(teamHistoryFilterModel) != -1) {
          this._filterCollection[this.systemFilterIndex(teamHistoryFilterModel)].values =
            teamHistoryFilterModel.values;
        } else {
          this._filterCollection.push(teamHistoryFilterModel);
        }
      }
    });
  }
  private setEvaluationQuestionFields() {
    this.attributeService.attributes
      .pipe(
        map((attributes) => {
          if (!attributes) return null;
          return attributes.filter(
            (a) => a.entityType == AttributeEntityType.Evaluation && a.filterable
          );
        })
      )
      .subscribe((result: AttributeDefinition[]) => {
        if (result && result.length > 0) {
          let evaluationQuestionFields = result.filter(
            (field) =>
              !this._filterCollection.find(
                (x) => x.displayName == field.name && x.targetFieldId == field.id
              )
          );

          evaluationQuestionFields.forEach((item) => {
            let searchValueType: SearchValueType;

            if (item.isEnumerated) {
              searchValueType = SearchValueType.EvaluationAggregateMultiValue;
            } else if (
              item.isOfUiType([
                AttributeUiType.LongAnswer,
                AttributeUiType.ShortAnswer,
                AttributeUiType.Number,
                AttributeUiType.Date
              ])
            ) {
              searchValueType = SearchValueType.EvaluationAggregateString;
            } else if (item.uiType == AttributeUiType.StarRating) {
              searchValueType = SearchValueType.Number;
            } else {
              return;
            }

            let evaluationAggregateFieldFilterModel = new EvaluationAggregateFieldFilterModel(
              item.name,
              item.id,
              searchValueType,
              item.valueList.map((x) => new ODataFilterValues(x.id, x.itemValue))
            );

            this._filterCollection.push(evaluationAggregateFieldFilterModel);
          });
        }

        this.filterCollection.next(this._filterCollection);
      });
  }
  private applySystemFieldVisibility(filters: any[]): any[] {
    return filterBySystemFieldVisibility(filters, this.appConfigService.tenant?.settings);
  }
  private setOtherFilters() {
    this.lookupService.countries.subscribe((result) => {
      if (!result) return;
      let countryFilterModel = PEOPLE_FILTER_MODELS.CountryFilterModel;
      countryFilterModel.values = result.map((x) => new ODataFilterValues(x.name, x.name));
      if (this.systemFilterIndex(countryFilterModel) != -1) {
        this._filterCollection[this.systemFilterIndex(countryFilterModel)].values =
          countryFilterModel.values;
      } else {
        this._filterCollection.push(countryFilterModel);
      }
    });

    this.lookupService.states.subscribe((result) => {
      if (!result) return;
      let stateFilterModel = PEOPLE_FILTER_MODELS.StateFilterModel;
      stateFilterModel.values = result.map((x) => new ODataFilterValues(x.name, x.name));
      if (this.systemFilterIndex(stateFilterModel) != -1) {
        this._filterCollection[this.systemFilterIndex(stateFilterModel)].values =
          stateFilterModel.values;
      } else {
        this._filterCollection.push(stateFilterModel);
      }
    });

    this.lookupService.races.subscribe((result) => {
      if (!result) return;
      const raceFieldEnabled = isSystemFieldEnabled(
        PeopleFilterFields.races,
        this.appConfigService.tenant?.settings
      );
      if (!raceFieldEnabled) return;
      let rcaeFilterModel = PEOPLE_FILTER_MODELS.RaceFilterModel;
      rcaeFilterModel.values = result.map((x) => new ODataFilterValues(x.id, x.name));
      if (this.systemFilterIndex(rcaeFilterModel) != -1) {
        this._filterCollection[this.systemFilterIndex(rcaeFilterModel)].values =
          rcaeFilterModel.values;
      } else {
        this._filterCollection.push(rcaeFilterModel);
      }
    });

    this.tagService.tags.subscribe((result) => {
      if (!result || !result.length) return;
      let model = PEOPLE_FILTER_MODELS.TagFilterModel;
      model.values = result.map((x) => new ODataFilterValues(x.text, x.text));
      if (this.systemFilterIndex(model) != -1) {
        this._filterCollection[this.systemFilterIndex(model)].values = model.values;
      } else {
        this._filterCollection.push(model);
      }
    });

    this.formService.forms
      .pipe(map((x) => x.filter((f) => f.isApplication(null) && f.isArchived == false)))
      .subscribe((result) => {
        if (!result || !result.length) {
          if (this.formsRequested == false) {
            this.formsRequested = true;
            this.formService.getAll();
          }
          return;
        }
        this.formsRequested = true;
        let model1 = PEOPLE_FILTER_MODELS.SubmittedApplicationFilterModel;
        model1.values = result.map((x) => new ODataFilterValues(x.id, x.title));
        if (this.systemFilterIndex(model1) != -1) {
          this._filterCollection[this.systemFilterIndex(model1)].values = model1.values;
        } else {
          this._filterCollection.push(model1);
        }

        let model2 = PEOPLE_FILTER_MODELS.LineupFormsFilterModel;
        model2.values = result.map((x) => new ODataFilterValues(x.id, x.title));
        let subFilters = [
          LINEUP_FORMS_SUB_FILTER_MODELS.LineupFormNameFilterModel,
          LINEUP_FORMS_SUB_FILTER_MODELS.SubmissionStatusFilterModel,
          LINEUP_FORMS_SUB_FILTER_MODELS.SentDateFilterModel,
          LINEUP_FORMS_SUB_FILTER_MODELS.SubmissionDateFilterModel,
          LINEUP_FORMS_SUB_FILTER_MODELS.SourceFilterModel
        ];
        model2.subFilter = new ODataSubFilter(subFilters);
        if (this.systemFilterIndex(model2) != -1) {
          this._filterCollection[this.systemFilterIndex(model2)].values = model2.values;
        } else {
          this._filterCollection.push(model2);
        }
      });

    this.personDetailsService.getCertifications().subscribe((result) => {
      if (!result || !result.length) return;
      let model1 = PEOPLE_FILTER_MODELS.ActiveCertificationFilterModel;
      model1.values = result.map((x) => new ODataFilterValues(x.id, x.name));

      if (this.systemFilterIndex(model1) != -1) {
        this._filterCollection[this.systemFilterIndex(model1)].values = model1.values;
      } else {
        this._filterCollection.push(model1);
      }

      let model2 = PEOPLE_FILTER_MODELS.CertificationsFilterModel;
      model2.values = result.map((x) => new ODataFilterValues(x.id, x.name));
      let subFilters = [
        ACTIVE_CERTIFICATION_SUB_FILTER_MODELS.CertificationNameFilterModel,
        ACTIVE_CERTIFICATION_SUB_FILTER_MODELS.CertificationNumberFilterModel,
        ACTIVE_CERTIFICATION_SUB_FILTER_MODELS.IssueDateFilterModel,
        ACTIVE_CERTIFICATION_SUB_FILTER_MODELS.ExpirationDateFilterModel,
        ACTIVE_CERTIFICATION_SUB_FILTER_MODELS.CertificationUrlFilterModel
      ];
      model2.subFilter = new ODataSubFilter(subFilters);

      if (this.systemFilterIndex(model2) != -1) {
        this._filterCollection[this.systemFilterIndex(model2)].values = model2.values;
      } else {
        this._filterCollection.push(model2);
      }
    });

    this.teamService.teams.subscribe((result) => {
      if (!result || !result.length) return;
      let teamExperienceFilterModel = PEOPLE_FILTER_MODELS.TeamExperienceFilterModel;
      teamExperienceFilterModel.values = result.map((x) => new ODataFilterValues(x.id, x.name));
      if (this.systemFilterIndex(teamExperienceFilterModel) != -1) {
        this._filterCollection[this.systemFilterIndex(teamExperienceFilterModel)].values =
          teamExperienceFilterModel.values;
      } else {
        this._filterCollection.push(teamExperienceFilterModel);
      }
    });

    this.workspaceService.currentWorkspaces.subscribe((result) => {
      if (!result || !result.workspaces.length || result.activeWorkspace.id != null) return;
      if (result.workspaces.length == 1 && result.workspaces[0].id == null) return; // if the only workspace is org.
      let filterableWorkspaces = result.workspaces.filter((w) => w.id != null);
      let personWorkspaceFilterModel = PEOPLE_FILTER_MODELS.WorkspaceFilterModel;
      personWorkspaceFilterModel.values = filterableWorkspaces.map(
        (x) => new ODataFilterValues(x.id, x.name)
      );
      if (this.systemFilterIndex(personWorkspaceFilterModel) != -1) {
        this._filterCollection[this.systemFilterIndex(personWorkspaceFilterModel)].values =
          personWorkspaceFilterModel.values;
      } else {
        this._filterCollection.push(personWorkspaceFilterModel);
      }
    });
  }

  private setCredentialFilters() {
    let credentialFilterModel = PEOPLE_FILTER_MODELS.PersonCredentialFilterModel;

    if (this.systemFilterIndex(credentialFilterModel) == -1) {
      this.adminCredentialService
        .getAllCredentialsNames(CredentialDefinitionType.Person)
        .then(async (credentials) => {
          credentialFilterModel.values = credentials.map(
            (x) => new ODataFilterValues(x.id, x.name)
          );

          credentialFilterModel.subFilter = new ODataSubFilter([
            PERSON_CREDENTIALS_SUB_FILTER_MODELS.IssueDateFilterModel,
            PERSON_CREDENTIALS_SUB_FILTER_MODELS.ExpirationDateFilterModel,
            PERSON_CREDENTIALS_SUB_FILTER_MODELS.CredentialStatusFilterModel,
            PERSON_CREDENTIALS_SUB_FILTER_MODELS.CredentialNumberFilterModel
          ]);

          const specialties = await this.adminCredentialService.getAllSpecialties(
            CredentialDefinitionType.Person
          );
          if (specialties && specialties.length > 0) {
            const specialtyFilterModel =
              PERSON_CREDENTIALS_SUB_FILTER_MODELS.CredentialSpecialtyFilterModel;
            specialtyFilterModel.values = specialties.map(
              (s) => new ODataFilterValues(s.id, s.name)
            );
            credentialFilterModel.subFilter.filters.push(specialtyFilterModel);
          }

          this._filterCollection.push(credentialFilterModel);
        });
    }
  }

  private hasNonActiveStatusFilter(appliedFilter: SelectedFilter): boolean {
    const condition1 = appliedFilter.filter.targetField == PeopleFilterFields.status;

    const condition2 =
      appliedFilter.filterOperator.displayOperator == FilterOperatorType.ne &&
      appliedFilter.searchValue == PersonStatus.Active;

    const condition3 =
      appliedFilter.filterOperator.displayOperator == FilterOperatorType.eq &&
      appliedFilter.searchValue == PersonStatus.Archived;

    const condition4 =
      appliedFilter.filterOperator.displayOperator == FilterOperatorType.eq &&
      appliedFilter.searchValue == PersonStatus.Potential;

    return condition1 && (condition2 || condition3 || condition4);
  }

  private hasArchivedStatusFilter(appliedFilter: SelectedFilter): boolean {
    const condition1 = appliedFilter.filter.targetField == PeopleFilterFields.status;

    const condition2 =
      appliedFilter.filterOperator.displayOperator == FilterOperatorType.eq &&
      appliedFilter.searchValue == PersonStatus.Archived;

    const condition3 =
      appliedFilter.filterOperator.displayOperator == FilterOperatorType.ne &&
      appliedFilter.searchValue == PersonStatus.Active;

    const condition4 =
      appliedFilter.filterOperator.displayOperator == FilterOperatorType.ne &&
      appliedFilter.searchValue == PersonStatus.Potential;

    return condition1 && (condition2 || (condition3 && condition4));
  }
}
